import React, { useEffect, useState } from "react";
import { EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import AddVendor from "./AddVendor";
import { deleteVendorById, getAllVendors } from "../../../api/api";
import { ScaleLoader } from "react-spinners";
import toast from "react-hot-toast";
import ConfirmDialog from "../../Delete/ConfirmDialog";

const VendorTable = () => {
  const [addvendorOpen, setAddvendorOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentVendor, setCurrentVendor] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);

  // Debounced Search
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  const rowsPerPage = 5; // If you add pagination later
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const handleAddVendor = () => {
    setCurrentVendor(null);
    setAddvendorOpen(true);
  };

  const handleCloseAddVendor = () => {
    setAddvendorOpen(false);
    setIsView(false);
    setIsEdit(false);
  };

  const handleView = (vendor) => {
    setIsEdit(false);
    setIsView(true);
    setCurrentVendor(vendor);
    setAddvendorOpen(true);
  };

  const handleEdit = (vendor) => {
    setIsEdit(true);
    setIsView(false);
    setCurrentVendor(vendor);
    setAddvendorOpen(true);
  };

  const handleDeleteClick = (vendor) => {
    setVendorToDelete(vendor);
    setConfirmDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (vendorToDelete) {
      deleteVendorById(vendorToDelete._id)
        .then((res) => {
          toast.success(res.message);
          setVendors((prev) =>
            prev.filter((vendor) => vendor._id !== vendorToDelete._id)
          );
          setVendorToDelete(null);
          fetchVendors(); // Refresh list
        })
        .catch((err) => toast.error(err.message));
    }
    setConfirmDialogOpen(false);
  };

  // Debounce logic
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1); // Reset to first page
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch vendors
  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await getAllVendors(currentPage, rowsPerPage, debouncedSearch);
      setVendors(res.data.vendors);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      toast.error("Failed to fetch vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [addvendorOpen, currentPage, debouncedSearch]);

  return (
    <>
      {!addvendorOpen ? (
        <>
          {/* Header and Search */}
          <div className="flex justify-between items-center px-4">
            <h1 className="text-2xl font-bold">Vendors</h1>
            <div className="flex gap-2 items-center">
              <input
                type="text"
                placeholder="Search by name/contact/company"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border px-2 py-1 rounded text-sm"
              />
              <button
                className="rounded-md py-2 px-4 bg-black text-white text-xs uppercase"
                onClick={handleAddVendor}
              >
                Add Vendor
              </button>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center items-center min-h-[300px]">
              <ScaleLoader />
            </div>
          ) : (
            <div className="w-full overflow-x-auto mt-4 px-4">
              <table className="w-full text-sm md:text-base">
                <thead>
                  <tr className="text-xs tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                    <th className="px-4 py-3">S.No</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Company Name</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Address</th>
                    <th className="px-4 py-3">Company Photo</th>
                    <th className="px-4 py-3 hidden md:table-cell">GST No</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y">
                  {vendors.length > 0 ? (
                    vendors.map((vendor, index) => (
                      <tr key={vendor._id} className="text-gray-700">
                        <td className="px-4 py-3">
                          {(currentPage - 1) * rowsPerPage + index + 1}
                        </td>
                        <td className="px-4 py-3 font-semibold">{vendor.name}</td>
                        <td className="px-4 py-3">{vendor.companyName}</td>
                        <td className="px-4 py-3">{vendor.contactNo}</td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {vendor.address}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {vendor.companyPhoto ? (
                            <img
                              src={vendor.companyPhoto}
                              alt="Company"
                              className="h-12 w-12 object-cover rounded border"
                            />
                          ) : (
                            <div className="h-8 w-8 bg-gray-200 rounded"></div>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">{vendor.gstNo}</td>
                        <td className="px-4 py-3 flex space-x-2 justify-center">
                          <EyeIcon
                            className="h-5 w-5 text-gray-500 hover:text-black cursor-pointer"
                            onClick={() => handleView(vendor)}
                          />
                          <PencilIcon
                            className="h-5 w-5 text-gray-500 hover:text-black cursor-pointer"
                            onClick={() => handleEdit(vendor)}
                          />
                          <TrashIcon
                            className="h-5 w-5 text-gray-500 hover:text-black cursor-pointer"
                            onClick={() => handleDeleteClick(vendor)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-gray-400">
                        No vendors found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-4 space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-1 rounded ${
                    currentPage === i + 1 ? "bg-black text-white" : "bg-gray-100"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}

          {/* Delete Confirmation */}
          <ConfirmDialog
            isOpen={confirmDialogOpen}
            onClose={() => setConfirmDialogOpen(false)}
            onConfirm={handleDeleteConfirm}
            title="Delete Vendor"
            message="Are you sure you want to delete this vendor?"
          />
        </>
      ) : (
        <AddVendor
          onBack={handleCloseAddVendor}
          isEdit={isEdit}
          isView={isView}
          vendor={currentVendor}
        />
      )}
    </>
  );
};

export default VendorTable;
